import type { Features } from '@backmarket/nuxt-module-marketplace/features'

import type { NavigationItem } from '../types'

import { ItemType, SEASONAL_HIGHLIGHT_COLOR } from './mapMenuItem'
import { isBlackFridaySeason, isHolidaysSeason } from './seasonsHelpers'

export const SEASONAL_ITEM_IDS = {
  GOOD_DEALS: 'good-deals',
  BLACK_FRIDAY: 'black-friday',
  HOLIDAYS: 'holidays',
} as const

export type SeasonalItemId =
  (typeof SEASONAL_ITEM_IDS)[keyof typeof SEASONAL_ITEM_IDS]

export type SeasonalFeatures = Pick<
  Features,
  'goodDeals' | 'blackFriday' | 'holidays'
>

const SEASONAL_IDS: readonly string[] = Object.values(SEASONAL_ITEM_IDS)

function isSeasonalItemId(id: string): id is SeasonalItemId {
  return SEASONAL_IDS.includes(id)
}

const DISABLED_SEASONAL_FEATURES: SeasonalFeatures = {
  goodDeals: { enabled: false },
  blackFriday: { enabled: false },
  holidays: { enabled: false },
}

const SEASONAL_DISPLAY_RULES: Record<
  SeasonalItemId,
  (features: SeasonalFeatures, now: Date) => boolean
> = {
  [SEASONAL_ITEM_IDS.GOOD_DEALS]: (features, now) =>
    features.goodDeals.enabled &&
    !isHolidaysSeason(now) &&
    !isBlackFridaySeason(now),
  [SEASONAL_ITEM_IDS.BLACK_FRIDAY]: (features, now) =>
    features.goodDeals.enabled &&
    features.blackFriday.enabled &&
    isBlackFridaySeason(now),
  [SEASONAL_ITEM_IDS.HOLIDAYS]: (features, now) =>
    features.goodDeals.enabled &&
    features.holidays.enabled &&
    isHolidaysSeason(now),
}

export function buildSeasonalNavigationItems(options: {
  locale: string
  titles: Record<SeasonalItemId, string>
}): NavigationItem[] {
  return Object.values(SEASONAL_ITEM_IDS).map((id) => ({
    title: options.titles[id],
    children: [],
    id,
    color: SEASONAL_HIGHLIGHT_COLOR,
    icon: 'Sparkles',
    type: ItemType.EVENT,
    url: `/${options.locale}/e/${id}`,
  }))
}

export function filterSeasonalNavigationItems(
  items: NavigationItem[],
  options: { features?: SeasonalFeatures; now: Date },
): NavigationItem[] {
  const features = options.features ?? DISABLED_SEASONAL_FEATURES

  return items.filter((item) => {
    if (!isSeasonalItemId(item.id)) {
      return true
    }

    return SEASONAL_DISPLAY_RULES[item.id](features, options.now)
  })
}
